import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useStore, StoreTypes } from "context";
import * as types from "constants/actionTypes";
import styles from "./index.module.scss";
import classnames from "classnames";
import Button from "@material-ui/core/Button";
import { useFetchBooks } from "customHooks/book";
import { Roles } from "constants/role";
import AnnotationModal from "../AnnotationModal";
import WarningDialog from "../WarningDialog";
import { AnnotationType } from "constants/annotationTypes";
import Repository from "repositories/Repository";
import Card from "components/Bookshelf/ReferenceBookShelf/Card";
import Pagination from "components/BookStore/ReferenceBookStore/Pagination";
import BitrixService from "components/BitrixService";
import Autocomplete from "components/BookStore/ReferenceBookStore/Autocomplete";
import useSetState from "customHooks/setState";
import { convertArrayToMap, groupArray } from "util/array";
import {
  useUpdateUserSettings,
  useUpdateUserFavorites,
  useDeleteUserFavorites,
} from "customHooks/userSetting";
import { UserSettingsAPI } from "api/UserSettingsAPI";
import Snackbar from "@material-ui/core/Snackbar";
import Header from "components/BookStore/ReferenceBookStore/Header";
import Footer from "components/BookStore/ReferenceBookStore/Footer";
import LoginPopup from "components/Login/Popup";
import CheckOnlinePopup from "components/Login/CheckOnlinePopup";
import { scrollTo } from "util/scrollTo";
import {
  subjectTable,
  categoryTable
} from "constants/optionTable";
import Loading from "components/BookStore/ReferenceBookStore/Loading";
import { useHistory } from "react-router-dom";
import { excludedTags } from "components/BookStore/ReferenceBookStore";

export const ReferenceBookshelf = () => {
  const [{ isOnLineDialog }, readerDispatch] = useStore(StoreTypes.reader);
  const history = useHistory();
  const [, bookDispatch] = useStore(StoreTypes.books);
  const [{ role, token, isLogin }] = useStore(StoreTypes.user);
  const [, annotationDispatch] = useStore(StoreTypes.annotation);
  const [, canvasDispatch] = useStore(StoreTypes.canvas);
  const [selectBookID, setSelectBookID] = useState("");
  const { getShelfBook, getBookTags, getPurchasedProducts } = useFetchBooks();
  const [isAnnotationTabOpen, setAnnotationTabOpen] = useState(false);
  const router = useStore(StoreTypes.router);
  const { BookContentRepository } = Repository;
  const updateUserSettings = useUpdateUserSettings();
  const updateUserFavorites = useUpdateUserFavorites();
  const deleteUserFavorites = useDeleteUserFavorites();
  const [bookTags, setBookTags] = useState({});
  const [{ books }] = useStore(StoreTypes.settings);
  const root = document.getElementById("root");
  const [{ SchoolYear, EducationalSystem, Subject, Grade, Category }, setTag] =
    useSetState({
      SchoolYear: [],
      EducationalSystem: [],
      Subject: [],
      Grade: [],
      Category: [],
    });
  const [currentShelfList, setCurrentShelfList] = useState({
    lists: [],
    total: 0,
    currentIndex: null,
    isLoading: true,
  });
  const [deleteShelfDialog, setShelfDialog] = useState({
    bookId: null,
    status: false,
    id: null,
  });
  // tag 初始選項
  const [initTag, setInitTag] = useState({});
  // 篩選 tag
  const [filterTag, setFilterTag] = useState([]);
  // 篩選 tag 順序
  const [filterOrderTag, setFilterOrderTag] = useState([]);
  // const { firebaseInitialize } = useFirebase();
  const qrcodeId = document.cookie
    .split(";")
    .find((doc) => doc.includes("qrcodeId"));
  const qrcodeToken = document.cookie
    .split(";")
    .find((doc) => doc.includes("nani_oneclass_login_token"));
  useEffect(() => {
    if (qrcodeId && qrcodeToken) {
      history.push("/qrcodelogin");
    }
  }, [history, qrcodeId, qrcodeToken]);

  useEffect(() => {
    if (!isLogin) return;

    (async () => {
      const userSettings = await UserSettingsAPI.getUserSettings(token);
      const favorites = await UserSettingsAPI.getUserFavorites(token);

      if (userSettings.status === "success") {
        await updateUserSettings(userSettings.data, false);
      }

      if (favorites.status === "success") {
        if (!favorites.data.isUpdateOldBook) {
          const oldData = await getOldData();
          const oldCollectedBook = {};
          const currentTime = Date.now();
          Object.values(oldData).forEach((item) => {
            oldCollectedBook[item.id] = {
              collectedTime: currentTime,
              bookId: item.bookId,
              id: item.id,
            };
          });

          await updateUserFavorites({
            books: { ...oldCollectedBook, ...favorites.data.books },
            isUpdateOldBook: true,
          });
        } else {
          await updateUserFavorites(favorites.data, false);
        }
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLogin, updateUserFavorites]);

  // 為了解決點選書櫃工具回書櫃，再重新進書時，畫記會消失的問題而註解
  // useEffect(() => {
  //   readerDispatch({ type: types.SET_BOOK_PAGE_INDEX, pageIndex: 0 });
  //   annotationDispatch({ type: types.RESET_ANNOTATION_INFO });
  // }, [annotationDispatch, readerDispatch]);

  useEffect(() => {
    (async () => {
      const tags = await getBookTags();
      const dataMap = {
        Grade: tags
          .filter((tag) => tag.category === "Grade")
          .filter((tag) => excludedTags.Grade.indexOf(tag.label) === -1 && tag.label.includes("年級")),
        Subject: tags
          .filter((tag) => tag.category === "Subject")
          .filter((tag) => excludedTags.Subject.indexOf(tag.label) === -1),
        EducationalSystem: tags
          .filter((tag) => tag.category === "EducationalSystem")
          .filter(
            (tag) => excludedTags.EducationalSystem.indexOf(tag.label) === -1
          ),
        SchoolYear: tags.filter((tag) => tag.category === "SchoolYear"),
        Category: tags.filter(
          (tag) => tag.category === "Grade" && !tag.label.includes("年級")).map(tag => ({...tag, category: 'Category'})),
      };

      setInitTag(dataMap);
      setBookTags(dataMap);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const goBook = useCallback((bookId, interactiveObjectId) => {
    router.history.push({
      pathname: `/${bookId}`,
      search: interactiveObjectId
        ? `?interactiveObjectId=${interactiveObjectId}`
        : "",
    });
  });
  const openBook = ({ bookId, interactiveObjectId }) => {
    setSelectBookID(bookId);
    bookDispatch({ type: types.SET_BOOK_ID, payload: bookId });
    if (role === Roles.EDITOR) {
      annotationDispatch({
        type: types.UPDATE_ANNOTATION_INFO,
        annotationId: interactiveObjectId,
        annotationType: AnnotationType.INTERACTIVE_OBJECT,
      });

      readerDispatch({
        type: types.SET_FULL_WIDTH_INFO,
        fullWidthInfo: { mode: false },
      });
      goBook(bookId, interactiveObjectId);
    } else {
      setAnnotationTabOpen(true);
    }
  };

  const [state, setState] = React.useState({
    open: false,
    vertical: "bottom",
    horizontal: "center",
  });

  const { vertical, horizontal, open } = state;

  function handleClose() {
    setState({ ...state, open: false });
  }

  const changePage = (event, page) => {
    const tagData = [...SchoolYear, ...EducationalSystem, ...Subject, ...Grade, ...Category];
    fetchBook(books, tagData, page);
  };

  const pageCount = useMemo(() => {
    if (currentShelfList.total > 0) {
      return Math.ceil(currentShelfList.total / 10);
    }
    return 0;
  }, [currentShelfList]);

  const filterChange = (event, value, key) => {
    let tagArr = [];
    const optionObj = value;
    const category = key;

    if (optionObj === null) {
      const deleteOrderTagIndex = filterOrderTag.findIndex((item) => {
        return item.category === category;
      });

      if (deleteOrderTagIndex === -1) {
        setTag({ [category]: tagArr });
        return;
      }

      const updateOrderArr = [...filterOrderTag];

      updateOrderArr.splice(deleteOrderTagIndex, 1);
      setFilterOrderTag([...updateOrderArr]);
    } else {
      const isSkip =
        optionObj.category !== "SchoolYear" &&
        optionObj.label !== "低年級" &&
        optionObj.label !== "中年級" &&
        optionObj.label !== "高年級";
      tagArr.push(optionObj);

      if (filterOrderTag.length > 0 && isSkip) {
        const someIndex = filterOrderTag.findIndex(
          (item) => optionObj.category === item.category
        );
        if (someIndex !== -1) {
          const updateOrderArr = [...filterOrderTag];
          updateOrderArr[someIndex] = optionObj;
          setFilterOrderTag([...updateOrderArr]);
        } else {
          setFilterOrderTag([...filterOrderTag, ...tagArr]);
        }
      } else if (isSkip) {
        setFilterOrderTag([...filterOrderTag, ...tagArr]);
      }
    }

    setTag({ [category]: tagArr });
  };

  // 篩選 options
  const filterCategoryOptions = (tableType, options) => {
    let categoryTag;
    let subjectTag;
    if (
      tableType === "Subject" ||
      options.find((item) => item.category === "Subject")
    ) {
      const subjectOption = options.find((item) => item.category === "Subject");
      categoryTag = getOptionsData(
        "Category",
        subjectTable[subjectOption.label].Category
      );
    }

    if (
      tableType === "Category" ||
      options.find((item) => item.category === "Category")
    ) {
      const categoryOption = options.find(
        (item) => item.category === "Category"
      );
      subjectTag = getOptionsData(
        "Subject",
        categoryTable[categoryOption.label].Subject
      );
    }
    setBookTags({
      ...initTag,
      SchoolYear: initTag.SchoolYear,
      ...(categoryTag && { Category: categoryTag }),
      ...(subjectTag && { Subject: subjectTag }),
    });
  };

  // 取得 option 資訊
  const getOptionsData = (key, optionArr) => {
    const newOptions = initTag[key].filter((item) => {
      return optionArr.find((option) => {
        return option === item.label;
      });
    });
    return newOptions;
  };

  useEffect(() => {
    setFilterTag([...SchoolYear, ...EducationalSystem, ...Subject, ...Grade, ...Category]);
  }, [SchoolYear, EducationalSystem, Subject, Grade, Category]);

  const fetchBook = useCallback(
    (booksData, tagData, page = 1) => {
      const currentData = {
        lists: [],
        total: 0,
        currentIndex: page - 1,
        isLoading: false,
      };

      if (Object.keys(booksData).length === 0) {
        setCurrentShelfList(currentData);
        return;
      }

      const booksDataValue = Object.values(booksData);
      const bookIds = [];

      booksDataValue.forEach((item) => {
        bookIds.push(item.bookId);
      });
      // 撈資料
      const fetchAllData = async () => {
        const bookIdsGroup = groupArray(bookIds, 100);
        const allData = await Promise.all(
          bookIdsGroup.map((bookIds) => {
            return getShelfBook({ token, bookIds });
          })
        );
        return [].concat(...allData);
      };

      fetchAllData()
        .then((res) => {
          if (res.length === 0) {
            setCurrentShelfList(currentData);
            return;
          }
          return res;
        })
        .then((res) => {
          if (!res) return;
          const allData = res;

          // 判斷篩選
          let filterBook = [];
          if (tagData.length > 0) {
            const newTagData = tagData.map((item) => {
              return item.tagId;
            });

            const filterHasTagsKey = allData.filter((item) => {
              return item.tags && item.tags.length !== 0;
            });

            let filterData = [];

            filterData = filterHasTagsKey
              .filter((item) => {
                return item.tags.indexOf(newTagData[0]) > -1;
              })
              .filter((arr) => {
                if (newTagData[1]) {
                  return arr.tags.indexOf(newTagData[1]) > -1;
                } else {
                  return arr;
                }
              })
              .filter((arr) => {
                if (newTagData[2]) {
                  return arr.tags.indexOf(newTagData[2]) > -1;
                } else {
                  return arr;
                }
              })
              .filter((arr) => {
                if (newTagData[3]) {
                  return arr.tags.indexOf(newTagData[3]) > -1;
                } else {
                  return arr;
                }
              });
            filterBook = filterData;
          } else {
            filterBook = allData;
          }

          // 分組
          if (filterBook.length > 0) {
            const idObj = convertArrayToMap(Object.values(books), "bookId");
            const sortBook = filterBook
              .map((item) => {
                return { ...item, ...idObj[item.bookId] };
              })
              .sort((a, b) => {
                return b.collectedTime - a.collectedTime;
              });

            const groupBooks = groupArray(sortBook, 10);

            if (!groupBooks[currentData.currentIndex]) {
              currentData.currentIndex = currentData.currentIndex - 1;
            }

            const currentPageBooks = groupBooks[currentData.currentIndex];

            currentData.total = sortBook.length;
            currentData.lists = currentPageBooks.map((item) => {
              return { ...item, ...idObj[item.bookId] };
            });
            currentData.isLoading = false;
          }
          setCurrentShelfList({ ...currentShelfList, ...currentData });
          scrollTo(root, 0, 300);
        });
    },
    [books, currentShelfList, getShelfBook, root, token]
  );

  // 更新舊資料
  const getOldData = async () => {
    const fetchOldData = await getPurchasedProducts({ token });
    const filterOldData = fetchOldData.map((item) => {
      return {
        id: item.id,
        bookId: item.bookId,
      };
    });
    const formatOldData = convertArrayToMap(filterOldData, "id");

    return formatOldData;
  };

  useEffect(() => {
    if (!token || !books) return;

    const tagData = [...SchoolYear, ...EducationalSystem, ...Subject, ...Grade, ...Category];
    fetchBook(books, tagData, currentShelfList.currentIndex + 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [books, SchoolYear, EducationalSystem, Subject, Grade, Category, token]);

  const deleteBook = (id) => {
    const tmpData = { ...books };
    delete tmpData[id];

    deleteUserFavorites({ books: { ...tmpData } });

    setShelfDialog({
      bookId: null,
      status: false,
      id: null,
    });
  };

  // 刪除書櫃提示
  const alertShelfDialog = (bookId, id) => {
    setShelfDialog({
      bookId: bookId,
      status: true,
      id: id,
    });
  };

  useEffect(() => {
    const isFilter = filterOrderTag.length > 0;

    if (!isFilter) {
      filterCategoryOptions("init", filterOrderTag);
    } else {
      let tableCategory = filterOrderTag[0].category;
      filterCategoryOptions(tableCategory, filterOrderTag);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterOrderTag]);

  // 清空上一步下一步紀錄
  useEffect(() => {
    canvasDispatch({ type: types.RESET_UNDO_ANNOTATIONS });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <Header path={"我的書櫃"} />
      <LoginPopup />
      {/* {<BitrixService />} */}
      {isOnLineDialog && <CheckOnlinePopup />}
      <div className={styles.container}>
        <div className={classnames(styles.decoration, styles.tree)} />
        <div className={classnames(styles.decoration, styles.kite)} />
        {!token ? null : (
          <div className={styles.filter}>
            <div className={styles.filterContainer}>
              <Autocomplete
                title={"學制"}
                optionsData={bookTags.EducationalSystem}
                onChange={(event, value) =>
                  filterChange(event, value, "EducationalSystem")
                }
                choosed={filterOrderTag.find(
                  (tag) => tag.category === "EducationalSystem"
                )}
              />
              <Autocomplete
                title={"科目"}
                optionsData={bookTags.Subject}
                onChange={(event, value) =>
                  filterChange(event, value, "Subject")
                }
                choosed={filterOrderTag.find(
                  (tag) => tag.category === "Subject"
                )}
              />
              <Autocomplete
                title={"學年度"}
                optionsData={bookTags.SchoolYear}
                onChange={(event, value) =>
                  filterChange(event, value, "SchoolYear")
                }
                choosed={filterOrderTag.find(
                  (tag) => tag.category === "SchoolYear"
                )}
              />
              <Autocomplete
                title={"類別"}
                optionsData={bookTags.Category}
                onChange={(event, value) =>
                  filterChange(event, value, "Subject")
                }
                choosed={filterOrderTag.find(
                  (tag) => tag.category === "Category"
                )}
              />
              <Autocomplete
                title={"年級"}
                optionsData={bookTags.Grade}
                onChange={(event, value) => filterChange(event, value, "Grade")}
                choosed={filterOrderTag.find((tag) => tag.category === "Grade")}
              />
            </div>
          </div>
        )}
        <div className={styles.bookCardContainer}>
          {isLogin ? (
            <>
              {currentShelfList.isLoading ? (
                <Loading />
              ) : (
                <>
                  {isLogin && currentShelfList.lists.length > 0 && (
                    <ul className={styles.listContainer}>
                      {currentShelfList.lists.map((book) => (
                        <Card
                          book={book}
                          BookContentRepository={BookContentRepository}
                          openBook={openBook}
                          deleteBook={deleteBook}
                          alertShelfDialog={alertShelfDialog}
                          key={book.bookId}
                        />
                      ))}
                    </ul>
                  )}
                  {currentShelfList.lists.length === 0 &&
                    filterTag.length !== 0 &&
                    token && (
                      <div className={styles.default}>
                        <img
                          src="/img/referenceBookNoResult.svg"
                          alt="找不到符合搜尋條件的書本"
                        />
                        找不到符合搜尋條件的書本
                      </div>
                    )}
                  {currentShelfList.lists.length === 0 &&
                    filterTag.length === 0 &&
                    token && (
                      <div className={styles.default}>
                        <img
                          src="/img/referenceBookNoBook.svg"
                          alt="前往書城加入書籍"
                        />
                        <div className={styles.title}>目前尚未有任何書本</div>
                        <div className={styles.sub_title}>
                          請前往書城加入您要的書本
                        </div>
                        <div className={styles.go_box}>
                          <a
                            href="/bookstore"
                            rel="noopener noreferrer"
                            className={styles.go_onemall}
                          >
                            前往書城
                          </a>
                        </div>
                      </div>
                    )}
                </>
              )}
            </>
          ) : (
            <div className={styles.default}>
              <img src="/img/referenceBookNoLogin.svg" alt="請先登入" />
              <div className={styles.sub_title}>請先登入</div>
            </div>
          )}
          <WarningDialog
            open={deleteShelfDialog.status}
            type={"deleteBook"}
            handleCancel={() => {
              setShelfDialog({
                bookId: null,
                status: false,
                id: null,
              });
            }}
            handleConfirm={() => {
              deleteBook(deleteShelfDialog.id);
            }}
          />

          {isLogin && currentShelfList.lists.length > 0 && (
            <Pagination
              count={pageCount}
              page={currentShelfList.currentIndex + 1}
              onChange={changePage}
            />
          )}
        </div>

        {(role === Roles.TEACHER ||
          role === Roles.GUEST ||
          role === Roles.STUDENT ||
          role === Roles.PARENT) &&
          isAnnotationTabOpen && (
            <AnnotationModal
              open={isAnnotationTabOpen}
              setOpen={setAnnotationTabOpen}
              selectBookID={selectBookID}
              goBook={goBook}
              setState={setState}
            />
          )}

        <Snackbar
          anchorOrigin={{ vertical, horizontal }}
          key={`${vertical},${horizontal}`}
          open={open}
          onClose={handleClose}
        >
          <Button
            variant="contained"
            style={{
              backgroundColor: "#f50057",
              color: "#ccc",
              width: 200,
              height: 70,
              fontSize: 32,
            }}
          >
            請登入
          </Button>
        </Snackbar>
      </div>
      <Footer />
    </>
  );
};

export default ReferenceBookshelf;
